type RobotsPolicy = 'index,follow' | 'noindex,follow' | 'noindex,nofollow'

export type SeoMetadata = {
  title: string
  description: string
  siteName?: string
  canonicalUrl?: string
  imageUrl?: string
  imageAlt?: string
  robots?: RobotsPolicy
  ogType?: 'website' | 'product' | 'article'
  locale?: string
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>> | null
}

const SEO_MANAGED_ATTR = 'data-seo-managed'
const JSON_LD_SCRIPT_ID = 'seo-json-ld'

const getSiteOrigin = () => {
  const configured = (import.meta.env.VITE_SITE_URL || '').trim().replace(/\/+$/, '')
  if (configured) return configured
  if (typeof window !== 'undefined' && window.location.origin) return window.location.origin
  return ''
}

export const toAbsoluteUrl = (value: string) => {
  const normalized = value.trim()
  if (!normalized) return ''
  if (/^https?:\/\//i.test(normalized)) return normalized
  const origin = getSiteOrigin()
  if (!origin) return normalized
  if (normalized.startsWith('/')) return `${origin}${normalized}`
  return `${origin}/${normalized}`
}

const upsertMetaTag = (attribute: 'name' | 'property', key: string, content?: string) => {
  if (typeof document === 'undefined') return

  const selector = `meta[${attribute}="${key}"]`
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null

  if (!content) {
    if (existing?.getAttribute(SEO_MANAGED_ATTR) === 'true') existing.remove()
    return
  }

  const element = existing ?? document.createElement('meta')
  element.setAttribute(attribute, key)
  element.setAttribute('content', content)
  element.setAttribute(SEO_MANAGED_ATTR, 'true')

  if (!existing) {
    document.head.appendChild(element)
  }
}

const upsertCanonicalLink = (href?: string) => {
  if (typeof document === 'undefined') return

  const existing = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!href) {
    if (existing?.getAttribute(SEO_MANAGED_ATTR) === 'true') existing.remove()
    return
  }

  const element = existing ?? document.createElement('link')
  element.setAttribute('rel', 'canonical')
  element.setAttribute('href', href)
  element.setAttribute(SEO_MANAGED_ATTR, 'true')

  if (!existing) {
    document.head.appendChild(element)
  }
}

const upsertJsonLd = (payload?: Record<string, unknown> | Array<Record<string, unknown>> | null) => {
  if (typeof document === 'undefined') return

  const existing = document.getElementById(JSON_LD_SCRIPT_ID) as HTMLScriptElement | null
  if (!payload) {
    if (existing?.getAttribute(SEO_MANAGED_ATTR) === 'true') existing.remove()
    return
  }

  const element = existing ?? document.createElement('script')
  element.type = 'application/ld+json'
  element.id = JSON_LD_SCRIPT_ID
  element.setAttribute(SEO_MANAGED_ATTR, 'true')
  element.text = JSON.stringify(payload)

  if (!existing) {
    document.head.appendChild(element)
  }
}

export const applySeoMetadata = (meta: SeoMetadata) => {
  if (typeof document === 'undefined') return

  const canonicalUrl = meta.canonicalUrl ? toAbsoluteUrl(meta.canonicalUrl) : toAbsoluteUrl(window.location.pathname)
  const ogImage = meta.imageUrl ? toAbsoluteUrl(meta.imageUrl) : ''
  const ogImageAlt = meta.imageAlt?.trim() || meta.title
  const robots = meta.robots || 'index,follow'
  const locale = meta.locale || 'pt_BR'
  const ogType = meta.ogType || 'website'
  const twitterCard = ogImage ? 'summary_large_image' : 'summary'

  document.title = meta.title

  upsertMetaTag('name', 'description', meta.description)
  upsertMetaTag('name', 'robots', robots)
  upsertMetaTag('name', 'twitter:card', twitterCard)
  upsertMetaTag('name', 'twitter:title', meta.title)
  upsertMetaTag('name', 'twitter:description', meta.description)
  upsertMetaTag('name', 'twitter:url', canonicalUrl)
  upsertMetaTag('name', 'twitter:image', ogImage || undefined)
  upsertMetaTag('name', 'twitter:image:alt', ogImage ? ogImageAlt : undefined)

  upsertMetaTag('property', 'og:title', meta.title)
  upsertMetaTag('property', 'og:description', meta.description)
  upsertMetaTag('property', 'og:type', ogType)
  upsertMetaTag('property', 'og:url', canonicalUrl)
  upsertMetaTag('property', 'og:image', ogImage || undefined)
  upsertMetaTag('property', 'og:image:alt', ogImage ? ogImageAlt : undefined)
  upsertMetaTag('property', 'og:site_name', meta.siteName?.trim() || meta.title)
  upsertMetaTag('property', 'og:locale', locale)

  upsertCanonicalLink(canonicalUrl)
  upsertJsonLd(meta.jsonLd)
}
